import { BonusCash, BonusGold } from 'lib/api/bonusApiResponse';
import { BonusFreespinFunBonus, MappedBonusType } from 'types/dashboard/mappedBonusType';
import { Tooltip, TooltipPosition } from 'components/tooltip';
import { useGetLabelByKey, useGetLabelsByKeys } from 'hooks/useLingUI';

import { AnchorLink } from 'components/anchorLink';
import { MyBonusCms } from 'lib/datoCms/queries/getMyBonusPage';
import { NoResult } from 'components/noResult';
import { ProgressBar } from 'components/header/progressBar';
import { TEXT_HIDDEN } from 'utility/constant';
import type { Url } from 'components/mobileTabNavigation';
import format from 'date-fns/format';
import { getAmount } from 'utility/functions';
import styles from './BonusOverview.module.scss';
import { useMemo } from 'react';
import { useTypedSelector } from 'lib/centralStore';

export type BonusOverviewProps = {
  bonusResponse?: MappedBonusType;
  selectedTab: string;
  tabs: Array<any>;
  selectedBalance?: number;
  myBonusPage: MyBonusCms;
};

type ListType = BonusFreespinFunBonus | BonusGold | BonusCash;

export const BonusOverview = ({
  bonusResponse,
  selectedTab,
  selectedBalance,
  myBonusPage,
  tabs,
}: BonusOverviewProps) => {
  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  });

  const labelSaldoTotale = useGetLabelByKey('saldo-totale');
  const labelInfo = useGetLabelByKey('info');
  const labelScadenza = useGetLabelByKey('scade-il');
  const labelSaldo = useGetLabelByKey('saldo');
  const labelAndamento = useGetLabelByKey('andamento');
  const labelVediTutti = useGetLabelByKey('vedi-tutti');
  const [toolTipBonusCash, toolTipBonusGold, toolTipBonusFreespin, toolTipBonusFunbonus] = useGetLabelsByKeys([
    'tooltip-bonus-cash',
    'tooltip-bonus-gold',
    'tooltip-bonus-freespin',
    'tooltip-bonus-funbonus',
  ]);
  const isSaldoVisible = useTypedSelector((state) => state.dashboard.isSaldoVisible);
  const type = useMemo<string | undefined>(() => {
    const type = tabs.find((tab) => tab.id === selectedTab).linkBlock[0].href.split('/')[
      tabs.find((tab) => tab.id === selectedTab).linkBlock[0].href.split('/').length - 1
    ];
    return type;
  }, [tabs, selectedTab]);

  const selectedTabContent = useMemo<ListType[] | undefined>(() => {
    return type === 'funbonus'
      ? bonusResponse?.listFunbonus
      : type === 'freespin'
      ? bonusResponse?.listFreespin
      : type === 'gold'
      ? bonusResponse?.listBonusGold
      : type === 'cash'
      ? bonusResponse?.listBonusCash
      : [];
  }, [type, bonusResponse]);

  const handleTooltip = useMemo<string | undefined>(() => {
    return type === 'cash'
      ? toolTipBonusCash
      : type === 'gold'
      ? toolTipBonusGold
      : type === 'freespin'
      ? toolTipBonusFreespin
      : type === 'funbonus'
      ? toolTipBonusFunbonus
      : '';
  }, [toolTipBonusCash, toolTipBonusGold, toolTipBonusFreespin, toolTipBonusFunbonus, type]);

  const handleLink = useMemo<Url>(() => {
    return type === 'cash'
      ? myBonusPage.linkBonusCash
      : type === 'gold'
      ? myBonusPage.linkBonusGold
      : type === 'freespin'
      ? myBonusPage.linkFreespin
      : type === 'funbonus'
      ? myBonusPage.linkFunBonus
      : '';
  }, [myBonusPage.linkBonusCash, myBonusPage.linkBonusGold, myBonusPage.linkFreespin, myBonusPage.linkFunBonus, type]);

  return selectedBalance && selectedBalance > 0 ? (
    <div className={styles.container}>
      <div className={styles.bonusOverviewContainer}>
        <div className={styles.bonusOverviewHeader}>
          <div className={styles.bonusOverviewTitle}>{labelSaldoTotale}</div>
          <div>
            <Tooltip
              content={handleTooltip}
              position={TooltipPosition.BOTTOM_LEFT}
              iconColor="--color-primary"
              childrenColor="--color-primary"
            >
              {labelInfo}
            </Tooltip>
          </div>
        </div>
        <div className={styles.bonusOverviewAmount}>
          {isSaldoVisible
            ? selectedTab && selectedBalance
              ? type === 'freespin'
                ? selectedBalance + ' spin'
                : formatter.format(getAmount(selectedBalance))
              : formatter.format(getAmount(0))
            : TEXT_HIDDEN}
        </div>
      </div>
      <div className={styles.overviewContainer}>
        {
          //bonus cash
          type === 'cash' &&
            selectedTabContent?.slice(0, 3).map((data, key) => (
              <div key={key} className={styles.bonusRow}>
                <div className={styles.bonusOverviewContent}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>
                      {!(data as BonusCash).dataTermine
                        ? null
                        : `${labelScadenza} ${' '}` + format(new Date((data as BonusCash).dataTermine!), 'dd/MM/yyyy')}
                    </span>
                    <p className={styles.sectionValue}>
                      {(data as BonusCash).verticali.length > 0
                        ? (data as BonusCash).verticali.reduce((acc, res) => {
                            return `${acc}, ${res}`;
                          })
                        : '-'}
                    </p>
                  </div>
                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>{labelSaldo}</span>
                    <p className={styles.sectionValueAmount}>
                      {isSaldoVisible ? formatter.format(getAmount((data as BonusCash).saldoResiduo!)) : TEXT_HIDDEN}
                    </p>
                  </div>
                </div>
              </div>
            ))
        }
        {
          //bonus gold
          type === 'gold' &&
            selectedTabContent?.slice(0, 2).map((data, key) => (
              <div key={key} className={styles.bonusRow}>
                <div className={styles.bonusOverviewContent}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>
                      {!(data as BonusGold).data_scadenza
                        ? null
                        : `${labelScadenza} ${' '}` +
                          format(new Date((data as BonusGold).data_scadenza!), 'dd/MM/yyyy')}
                    </span>
                    <p className={styles.sectionValue}>{(data as BonusGold).bonus_nome}</p>
                  </div>

                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>{labelSaldo}</span>
                    <p className={styles.sectionValueAmount}>
                      {isSaldoVisible ? formatter.format(getAmount((data as BonusGold).impo_bonus_w!)) : TEXT_HIDDEN}
                    </p>
                  </div>
                </div>
                <div className={styles.progressBarContainer}>
                  <div className={styles.infoBonusContainer}>
                    <span>{labelAndamento}</span>
                    <ProgressBar
                      progress={getAmount((data as BonusGold).wr_count!)}
                      maxLimit={getAmount((data as BonusGold).wr!)}
                      currencyType="EUR"
                    />
                  </div>
                </div>
              </div>
            ))
        }
        {
          //funbonus e freespin
          (type === 'funbonus' || type === 'freespin') &&
            selectedTabContent?.slice(0, 3).map((data, key) => (
              <div key={key} className={styles.bonusRow}>
                <div className={styles.bonusOverviewContent}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>
                      {!(data as BonusFreespinFunBonus).expirationDate
                        ? null
                        : `${labelScadenza} ${' '}` +
                          format(new Date((data as BonusFreespinFunBonus).expirationDate!), 'dd/MM/yyyy')}
                    </span>
                    <p className={styles.sectionValue}>
                      {(data as BonusFreespinFunBonus).title && (data as BonusFreespinFunBonus).title !== ''
                        ? (data as BonusFreespinFunBonus).title
                        : '-'}
                    </p>
                  </div>
                  <div className={styles.sectionTitle}>
                    <span className={styles.label}>{labelSaldo}</span>
                    <p className={styles.sectionValueAmount}>
                      {isSaldoVisible
                        ? selectedTab === '125753600'
                          ? formatter.format(getAmount((data as BonusFreespinFunBonus).amount!))
                          : (data as BonusFreespinFunBonus).amount + ' spin'
                        : TEXT_HIDDEN}
                    </p>
                  </div>
                </div>
              </div>
            ))
        }
      </div>
      <AnchorLink href={handleLink} className={styles.seeAll}>
        {labelVediTutti}
      </AnchorLink>
    </div>
  ) : (
    <NoResult label={myBonusPage?.nessunRisultato} transparent={true} />
  );
};
